import React, { useContext, useState } from "react";
import Axios from "axios";
import { useSnackbar } from "notistack";

import { UserContext } from "./UserContext";
import { DatabaseContext } from "./DatabaseContext";

export const OrderContext = React.createContext({});

export const OrderProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const { apiUrl } = useContext(DatabaseContext);
  const { Site, email } = useContext(UserContext);
  const {
    siteCode,
    siteType,
    barcodeEnabled,
    autoBarcode,
    barcodePrefix,
    barcodeSuffix,
  } = Site;

  const getAllSiteOrders = async () => {
    try {
      const res = await Axios.get(`${apiUrl}/api/orders/${Site.siteCode}`);
      if (res.data.success) {
        return res.data.data;
      } else {
        enqueueSnackbar(res.data.error, {
          variant: "error",
          autoHideDuration: 6000,
        });
      }
    } catch (err) {
      enqueueSnackbar(err.message, {
        variant: "error",
        autoHideDuration: 6000,
      });
    }
  };

  const getAllPatronOrders = async (externalID) => {
    try {
      const res = await Axios.get(
        `${apiUrl}/api/orders/${Site.siteCode}/${externalID}`
      );
      if (res.data.success) {
        return res.data.data[0];
      } else {
        enqueueSnackbar(res.data.error, {
          variant: "error",
          autoHideDuration: 6000,
        });
      }
    } catch (err) {
      enqueueSnackbar(err.message, {
        variant: "error",
        autoHideDuration: 6000,
      });
    }
  };

  const addPatronToOrder = (patron) => {
    let filteredOrders = orders.filter(
      ({ externalID }) => externalID !== patron.externalID
    );
    const {
      externalID,
      firstName,
      lastName,
      dateOfBirth,
      street,
      suburb,
      state,
      postcode,
      curriculum,
      custom1,
      custom2,
      custom3,
    } = patron;

    const printedID =
      Site.printedID === "externalID"
        ? externalID
        : Site.printedID === "curriculum"
        ? curriculum
        : Site.printedID === "custom1"
        ? custom1
        : Site.printedID === "custom2"
        ? custom2
        : Site.printedID === "custom3"
        ? custom3
        : "NOT PROVIDED";
    const printedBarcode =
      Site.printedBarcode === "externalID"
        ? externalID
        : Site.printedBarcode === "curriculum"
        ? curriculum
        : Site.printedBarcode === "custom1"
        ? custom1
        : Site.printedBarcode === "custom2"
        ? custom2
        : Site.printedBarcode === "custom3"
        ? custom3
        : "";

    const patronOrder = {
      siteCode,
      userEmail: email,
      externalID,
      lastName,
      firstName,
      dateOfBirth,
      street,
      suburb,
      state,
      postcode,
      custom1: custom1,
      custom2: custom2,
      custom3: custom3,
      printedID,
      barcode: barcodeEnabled
        ? `${autoBarcode ? barcodePrefix : ""}${printedBarcode}${
            autoBarcode ? barcodeSuffix : ""
          }`
        : "",
      cardType: siteType,
    };

    if (validOrder(patronOrder)) {
      filteredOrders.push(patronOrder);
      setOrders(filteredOrders);
    }
  };

  const validOrder = (patronOrder) => {
    var bResult = true;
    var sErrorMessage = "";

    if (patronOrder.externalID == " ") {
      bResult = false;
      sErrorMessage = `External ID can't be blank`;
    }
    if (patronOrder.firstName == " ") {
      bResult = false;
      sErrorMessage = `External ID can't be blank`;
    }
    if (patronOrder.lastName == " ") {
      bResult = false;
      sErrorMessage = `External ID can't be blank`;
    }
    if (siteType === 0) {
      if (
        patronOrder.street == " " ||
        patronOrder.suburb == " " ||
        patronOrder.state == " " ||
        patronOrder.postcode == " "
      ) {
        bResult = false;
        sErrorMessage = `Address can't be blank`;
      }
    }

    if (bResult == false) {
      enqueueSnackbar(sErrorMessage, {
        variant: "error",
        autoHideDuration: 6000,
        style: { fontSize: "16px" },
      });
    }

    return bResult;
  };

  const addPatronsToOrder = (patronsArray) => {
    let allOrders = [];
    patronsArray.forEach((patron) => {
      const {
        externalID,
        firstName,
        lastName,
        dateOfBirth,
        street,
        suburb,
        state,
        postcode,
        curriculum,
        custom1,
        custom2,
        custom3,
      } = patron;

      const printedID =
        Site.printedID === "externalID"
          ? externalID
          : Site.printedID === "curriculum"
          ? curriculum
          : Site.printedID === "custom1"
          ? custom1
          : Site.printedID === "custom2"
          ? custom2
          : Site.printedID === "custom3"
          ? custom3
          : "NOT PROVIDED";
      const printedBarcode =
        Site.printedBarcode === "externalID"
          ? externalID
          : Site.printedBarcode === "curriculum"
          ? curriculum
          : Site.printedBarcode === "custom1"
          ? custom1
          : Site.printedBarcode === "custom2"
          ? custom2
          : Site.printedBarcode === "custom3"
          ? custom3
          : "";

      const patronOrder = {
        siteCode,
        userEmail: email,
        externalID,
        lastName,
        firstName,
        dateOfBirth,
        street,
        suburb,
        state,
        postcode,
        custom1: custom1,
        custom2: custom2,
        custom3: custom3,
        printedID,
        barcode: barcodeEnabled
          ? `${autoBarcode ? barcodePrefix : ""}${printedBarcode}${
              autoBarcode ? barcodeSuffix : ""
            }`
          : "",
        cardType: siteType,
      };
      allOrders.push(patronOrder);
    });
    setOrders(allOrders);
  };

  const removePatronFromOrder = (externalID) => {
    let filteredOrders = orders.filter(
      (order) => order.externalID !== externalID
    );

    setOrders(filteredOrders);
  };

  const clearOrders = () => {
    setOrders([]);
  };

  const sendOrder = async (orders, options) => {
    try {
      const res = await Axios.post(`${apiUrl}/api/orders`, { orders, options });
      if (res.data.success) {
        const { validOrders, invalidOrders } = res.data;
        if (validOrders.length > 0) {
          enqueueSnackbar(
            `${validOrders.length} card${validOrders !== 1 ? "s" : ""} ordered`,
            { variant: "success" }
          );
        }
        if (invalidOrders.length > 0) {
          enqueueSnackbar(
            `${invalidOrders.length} card${
              invalidOrders !== 1 ? "s" : ""
            } had errors`,
            { variant: "error", autoHideDuration: 6000 }
          );
        }
        return { validOrders, invalidOrders };
      } else {
        enqueueSnackbar(res.data.error, {
          variant: "error",
          autoHideDuration: 6000,
        });
      }
    } catch (err) {
      enqueueSnackbar(err.message, {
        variant: "error",
        autoHideDuration: 6000,
      });
    }
  };

  const FirstTimeOrderQueue = async () => {
    try {
      const res = await Axios.get(
        `${apiUrl}/api/library/firsttimeorderqueue/?siteCode=${siteCode}`
      );
      if (res.data.success) {
        setOrders(res.data.data[0]);
        return res.data.data[0];
      }
    } catch (err) {
      enqueueSnackbar(err.response.data.error, {
        variant: "error",
        autoHideDuration: 6000,
      });
    }
  };

  return (
    <OrderContext.Provider
      value={{
        orders,
        getAllSiteOrders,
        getAllPatronOrders,
        addPatronToOrder,
        addPatronsToOrder,
        removePatronFromOrder,
        clearOrders,
        sendOrder,
        FirstTimeOrderQueue,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};
